import React, { useState, useRef, useLayoutEffect } from 'react';
import { getAuth, sendEmailVerification } from 'firebase/auth';
import { Mail } from 'lucide-react';
import { useTrial } from '../contexts/TrialContext';
import { useCompany } from '../contexts/CompanyContext';
import { logger } from '../utils/logger';

/**
 * Gate leve: banner não-bloqueante mostrado quando o usuário está em trial e ainda
 * NÃO verificou o e-mail. Em vez de travar a plataforma inteira (modal), deixa explorar
 * e só lembra de confirmar — as ações-chave (ex.: ligar o bot em produção) é que exigem
 * a verificação (checado server-side). Some sozinho ao confirmar.
 */
const EmailVerifyBanner = () => {
  const { emailNotVerified, checkTrialStatus, setEmailNotVerified } = useTrial();
  const { selectedCompany } = useCompany();
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const bannerRef = useRef(null);

  // Publica a altura real do banner em --verify-banner-h no :root. As telas
  // de altura cheia (ChatLayout mobile, ChatSettings) descontam essa var —
  // sem isso o banner empurra o app pra baixo de 100dvh e corta o rodapé
  // (BottomNav/composer) no mobile.
  useLayoutEffect(() => {
    const root = document.documentElement;
    const el = bannerRef.current;
    if (!emailNotVerified || !el) {
      root.style.removeProperty('--verify-banner-h');
      return undefined;
    }
    const update = () => root.style.setProperty('--verify-banner-h', `${el.offsetHeight}px`);
    update();
    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(update);
      ro.observe(el);
    }
    return () => {
      if (ro) ro.disconnect();
      root.style.removeProperty('--verify-banner-h');
    };
  }, [emailNotVerified]);

  if (!emailNotVerified) return null;

  const resend = async () => {
    setMsg('');
    setBusy(true);
    try {
      const auth = getAuth();
      if (auth.currentUser) {
        await sendEmailVerification(auth.currentUser);
        setMsg('E-mail de verificação reenviado. Confira sua caixa de entrada (e o spam).');
      }
    } catch (e) {
      logger.warn?.('[EmailVerifyBanner] reenviar falhou:', e?.message);
      setMsg('Não foi possível reenviar agora. Tente novamente em alguns minutos.');
    } finally {
      setBusy(false);
    }
  };

  const check = async () => {
    setMsg('');
    setBusy(true);
    try {
      const auth = getAuth();
      if (auth.currentUser) {
        await auth.currentUser.reload();
        await auth.currentUser.getIdToken(true); // atualiza o claim email_verified no token
        if (auth.currentUser.emailVerified) {
          setEmailNotVerified(false);
          if (selectedCompany?.companyId) {
            await checkTrialStatus(selectedCompany.companyId);
          }
        } else {
          setMsg('Ainda não detectamos a verificação. Clique no link do e-mail e tente novamente.');
        }
      }
    } catch (e) {
      logger.warn?.('[EmailVerifyBanner] verificar falhou:', e?.message);
      setMsg('Erro ao verificar. Tente novamente.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      ref={bannerRef}
      role="status"
      style={{
        width: '100%',
        boxSizing: 'border-box',
        padding: '10px 16px',
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: '12px',
        background: 'linear-gradient(180deg, #FFF8E1 0%, #FFF3CD 100%)',
        borderBottom: '1px solid rgba(245,158,11,0.45)',
        color: '#0E0E12',
        fontSize: '14px',
        zIndex: 40,
      }}
    >
      <Mail size={18} color="#B7791F" style={{ flexShrink: 0 }} />
      <span style={{ flex: '1 1 240px', minWidth: 0 }}>
        <strong>Confirme seu e-mail</strong> para ativar o WhatsApp e colocar o bot em produção.
        Você pode explorar a plataforma normalmente enquanto isso.
      </span>
      <span style={{ display: 'flex', gap: '8px', alignItems: 'center', flexShrink: 0 }}>
        <button
          type="button"
          onClick={check}
          disabled={busy}
          style={{
            padding: '7px 14px',
            background: busy ? '#9A9AA5' : '#FFC107',
            color: '#0E0E12',
            border: 'none',
            borderRadius: '8px',
            fontSize: '13px',
            fontWeight: 600,
            cursor: busy ? 'not-allowed' : 'pointer',
          }}
        >
          {busy ? 'Verificando…' : 'Já verifiquei'}
        </button>
        <button
          type="button"
          onClick={resend}
          disabled={busy}
          style={{
            padding: '7px 12px',
            background: 'transparent',
            color: '#5A5A65',
            border: '1px solid rgba(0,0,0,0.15)',
            borderRadius: '8px',
            fontSize: '13px',
            fontWeight: 500,
            cursor: busy ? 'not-allowed' : 'pointer',
          }}
        >
          Reenviar e-mail
        </button>
      </span>
      {msg && (
        <span style={{ flexBasis: '100%', fontSize: '13px', color: '#5A5A65' }}>{msg}</span>
      )}
    </div>
  );
};

export default EmailVerifyBanner;
